"use client"

import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { trackEvent } from "@/lib/analytics"
import { setBottomDockHeight, useAdDockInset } from "@/hooks/use-bottom-dock-inset"

interface ResultStickySaveBarProps {
  /** The noun the user is saving — a grass name (identify) or a diagnosed
   *  problem name (diagnose). Drives the headline copy. */
  itemName: string
  /** Which flow this bar serves. Each kind keeps its own copy and analytics key. */
  kind?: "grass" | "problem" | "weed"
  /** Funnel dimension — mirrors the upgrade card's entry_path ("homepage" | "saved_result"). */
  entryPath: string
  /** Element whose visibility hides the bar and which receives the default click. */
  targetId?: string
  /** Override the save action. Default (no handler) keeps the legacy
   *  smooth-scroll to #care-plan-upgrade; the identify surface passes a handler
   *  that opens the direct save modal instead (2026-07-14 conversion pass). */
  onSaveClick?: () => void
  /** Result-funnel notification. Fires alongside (never instead of) this bar's
   *  own `result_sticky_save_clicked`, so the host can report the tap as the
   *  save action its `result_state_shown` advertised. */
  onNextAction?: () => void
}

/**
 * Close any open visibility span and return total on-screen milliseconds.
 * Module-level so the terminal effect can call it without capturing component
 * scope (it reads only the refs it is handed, which are stable).
 */
function settleVisibleMs(
  sinceRef: { current: number | null },
  totalRef: { current: number },
): number {
  if (sinceRef.current !== null) {
    totalRef.current += Date.now() - sinceRef.current
    sinceRef.current = null
  }
  return Math.round(totalRef.current)
}

/**
 * Mobile-only sticky bottom save bar for the anonymous result screen
 * (2026-07-06 conversion pass). Attacks the visibility leak: 70% of result
 * viewers never scrolled the save ask (#care-plan-upgrade) into view, so this
 * keeps one specific CTA on screen in the dead zone between the hero and the
 * digest ask.
 *
 * Behavior: hidden while the page is still near the top (the hero IS the CTA
 * frame there) and whenever the digest ask is on screen (no double ask in one
 * viewport); dismissible for the rest of the session. The owner gates
 * rendering to anon users with an active care-plan CTA, so this component only
 * handles scroll/visibility state.
 */
export function ResultStickySaveBar({ itemName, kind = "grass", entryPath, targetId = "care-plan-upgrade", onSaveClick, onNextAction }: ResultStickySaveBarProps) {
  // Preserve the identify funnel's `grass_type` key; diagnose reports `problem`
  // instead so the two surfaces read separately in the capture dashboard.
  const trackingProps = {
    ...(kind === "grass"
      ? { grass_type: itemName }
      : kind === "problem"
        ? { problem: itemName }
        : { weed: itemName }),
    entry_path: entryPath,
    // Normalized discriminator. The itemName key still varies by flow so the
    // existing per-flow dashboards keep working, but that shape forces one
    // query per flow; `kind` lets all three be read together.
    kind,
  }
  const headline = kind === "weed" ? `${itemName} control plan ready` : `Your ${itemName} plan is ready`
  const subline = kind === "grass"
    ? "Save it free before you go"
    : kind === "problem"
      ? "Save your treatment plan free"
      : "Keep the ID and next steps together"
  const buttonCopy = kind === "weed" ? "Save control plan" : "Save my plan"
  const [pastHero, setPastHero] = useState(false)
  const [askVisible, setAskVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const shownTrackedRef = useRef(false)
  const barRef = useRef<HTMLDivElement>(null)
  // Clears the Mediavine anchor/player beneath this bar. Must be the ad-only
  // inset, not the dock total, which includes this bar's own height. Ads are
  // blocked on /result/[id] today, so this reads 0 there; it is the correct
  // behavior if that route is ever monetized again.
  const adDockInset = useAdDockInset()
  // Exposure accounting. `result_sticky_save_shown` only says the bar appeared
  // at least once, which cannot distinguish "on screen for 40s and ignored"
  // (a copy/offer problem) from "on screen for 400ms while scrolling past"
  // (no real opportunity). Both produce the same impression today, and the
  // measured 0.9% click rate is unreadable without the split.
  const visibleSinceRef = useRef<number | null>(null)
  const visibleMsRef = useRef(0)
  const appearancesRef = useRef(0)
  const endTrackedRef = useRef(false)
  const trackingPropsRef = useRef(trackingProps)
  trackingPropsRef.current = trackingProps

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > 320)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Track the digest ask's viewport presence. The target is dynamic-imported,
  // so poll for the node until it mounts, then observe it.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return
    let observer: IntersectionObserver | null = null
    let cancelled = false

    const attach = () => {
      if (cancelled) return
      const target = document.getElementById(targetId)
      if (!target) {
        setTimeout(attach, 500)
        return
      }
      observer = new IntersectionObserver(
        ([entry]) => setAskVisible(entry.isIntersecting),
        // Fire a little early so the bar is gone by the time the ask is
        // meaningfully readable — no moment with two asks stacked.
        { rootMargin: "0px 0px -10% 0px" },
      )
      observer.observe(target)
    }
    attach()

    return () => {
      cancelled = true
      observer?.disconnect()
    }
  }, [targetId])

  const visible = pastHero && !askVisible && !dismissed

  // Register with the bottom-dock system so the chat FAB (and anything else
  // dock-aware) clears this bar instead of stacking under it. md:hidden makes
  // the desktop measurement 0, so this is a no-op above the mobile breakpoint.
  useEffect(() => {
    if (!visible) {
      setBottomDockHeight(0)
      return
    }
    const el = barRef.current
    if (!el) return
    const updateHeight = () => setBottomDockHeight(el.getBoundingClientRect().height)
    updateHeight()
    const ro = new ResizeObserver(updateHeight)
    ro.observe(el)
    return () => {
      ro.disconnect()
      setBottomDockHeight(0)
    }
  }, [visible])

  useEffect(() => {
    if (!visible || shownTrackedRef.current) return
    shownTrackedRef.current = true
    trackEvent("result_sticky_save_shown", trackingProps)
    // trackingProps is a fresh object each render but its values only change
    // when itemName/kind/entryPath do; the guard ref makes this fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // Accumulate on-screen time across every appearance. The bar re-hides
  // whenever the digest ask scrolls into view, so a session can contain
  // several spans and only their sum is meaningful.
  useEffect(() => {
    if (visible) {
      visibleSinceRef.current = Date.now()
      appearancesRef.current += 1
    } else {
      settleVisibleMs(visibleSinceRef, visibleMsRef)
    }
  }, [visible])

  // One terminal event per mount carrying the exposure totals. Without it the
  // dwell time dies with the page and the click rate stays uninterpretable.
  useEffect(() => {
    const emitEnd = (reason: string) => {
      if (endTrackedRef.current || !shownTrackedRef.current) return
      endTrackedRef.current = true
      trackEvent("result_sticky_save_hidden", {
        ...trackingPropsRef.current,
        visible_ms: settleVisibleMs(visibleSinceRef, visibleMsRef),
        appearances: appearancesRef.current,
        reason,
      })
    }
    const onPageHide = () => emitEnd("page_left")
    window.addEventListener("pagehide", onPageHide)
    return () => {
      window.removeEventListener("pagehide", onPageHide)
      emitEnd("unmounted")
    }
  }, [])

  const handleClick = () => {
    trackEvent("result_sticky_save_clicked", {
      ...trackingProps,
      visible_ms: settleVisibleMs(visibleSinceRef, visibleMsRef),
      appearances: appearancesRef.current,
    })
    onNextAction?.()
    // The bar stays mounted after a click, so the terminal event would
    // otherwise double-report this exposure as an ignore.
    endTrackedRef.current = true
    if (onSaveClick) {
      onSaveClick()
      return
    }
    document
      .getElementById(targetId)
      ?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  const handleDismiss = () => {
    setDismissed(true)
    trackEvent("result_sticky_save_dismissed", {
      ...trackingProps,
      visible_ms: settleVisibleMs(visibleSinceRef, visibleMsRef),
      appearances: appearancesRef.current,
    })
    endTrackedRef.current = true
  }

  return (
    <div
      ref={barRef}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 md:hidden",
        "border-t border-gray-200 bg-white/95 backdrop-blur-sm",
        "transition-all duration-300",
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0",
      )}
      // Safe-area value must stay inline (precompiled Tailwind bundle has no
      // JIT for env() arbitrary values).
      style={{
        padding: "10px 12px calc(10px + env(safe-area-inset-bottom))",
        bottom: adDockInset,
      }}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1 text-[11px] leading-tight text-gray-600">
          <span className="block truncate text-[12.5px] font-semibold text-gray-900">
            {headline}
          </span>
          {subline}
        </div>
        <button
          type="button"
          onClick={handleClick}
          tabIndex={visible ? 0 : -1}
          className="flex-shrink-0 rounded-xl bg-green-600 px-4 py-2.5 text-[13px] font-bold text-white shadow-md transition-colors hover:bg-green-700"
        >
          {buttonCopy}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          tabIndex={visible ? 0 : -1}
          aria-label="Dismiss"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" strokeWidth={2.25} />
        </button>
      </div>
    </div>
  )
}
